import { BranchCode } from "api/risk"
import { useSubscriptionProcessState } from "features/subscription-process/contexts/selectors/selectors"
import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { BranchStepConfigFactory, StepDirection } from "features/subscription-process/navigation/Steps/createStepConfig"


export type BranchStepConfig = Partial<Record<BranchCode, BranchStepConfigFactory>>

export const useStepNavigation = (branchStepConfig: BranchStepConfig) => {
  const { riskId, branchCode } = useSubscriptionProcessState()
  const navigate = useNavigate()

  const stepConfig = useMemo(() => {
    if (!branchCode || !riskId) return null
    return branchStepConfig[branchCode]?.(riskId) ?? null
  }, [branchStepConfig, riskId, branchCode])

  const navigateToStep = useCallback(
    (direction: StepDirection) => {
      if (!riskId || !branchCode) return

      // Step state is now managed by backend - just navigate
      if (direction.onClick) {
        direction.onClick()
      } else {
        navigate(direction.route, { state: { branchCode } })
      }
    },
    [branchCode, riskId, navigate]
  )

  const goToNext = useCallback(() => {
    if (stepConfig?.next) {
      navigateToStep(stepConfig.next)
    }
  }, [stepConfig, navigateToStep])

  const goToPrevious = useCallback(() => {
    if (stepConfig?.prev) {
      navigateToStep(stepConfig.prev)
    }
  }, [stepConfig, navigateToStep])

  return { stepConfig, goToNext, goToPrevious }
}
